import { Injectable, signal, Signal, computed } from '@angular/core';
import { DjangoPermission, ModulePermission, User } from '@core/models';
import { AuthService } from './auth.service';

/**
 * Permission Service
 * Provides permission checking utilities based on the authenticated user
 * Permissions are fetched as part of the user object from /auth/me/
 */
@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  /** Current user signal (reactive) */
  private readonly _user = signal<User | undefined>(undefined);

  /** Public computed signals */
  public readonly user: Signal<User | undefined> = this._user.asReadonly();
  public readonly userModules: Signal<ModulePermission[]> = computed(() => this._getUserModules());

  /**
   * The first load, so a guard can wait for it.
   *
   * `user` is a SIGNAL: reading it tells you what is known now, and awaiting it
   * resolves immediately with whatever that is. A guard that did
   * `await permissionService.user()` on a cold page therefore decided against a
   * user that had not arrived yet, and sent the first module route anyone
   * guarded straight to /access-denied (2026-09-24).
   */
  private _loaded: Promise<void>;

  constructor(private readonly _authService: AuthService) {
    // Initialize user on service creation
    this._loaded = this._initializeUser();
  }

  /** The user, once the initial load has finished. */
  public async ready(): Promise<User | undefined> {
    await this._loaded;
    return this._user();
  }

  /**
   * Initialize user from AuthService
   */
  private async _initializeUser(): Promise<void> {
    const user = await this._authService.user;
    this._user.set(user);
  }

  /**
   * Refresh user data (call after login/logout)
   */
  public async refreshUser(): Promise<void> {
    this._loaded = this._initializeUser();
    await this._loaded;
  }

  /**
   * Check if user has a specific permission
   * @param {DjangoPermission} permission - Full Django permission (e.g., "intake.view_prescription")
   * @returns {boolean} - True if user has the permission
   */
  public hasPermission(permission: DjangoPermission): boolean {
    const user = this._user();
    if (!user) return false;

    // Superusers have all permissions
    if (user.is_superuser) return true;

    return user.permissions.includes(permission);
  }

  /**
   * Check if user can access a specific module
   * @param {ModulePermission} module - Module permission codename (e.g., "view_orientador_module")
   * @returns {boolean} - True if user can access the module
   */
  public canAccessModule(module: ModulePermission): boolean {
    const user = this._user();
    if (!user) return false;

    // Superusers can access everything
    if (user.is_superuser) return true;

    // Check for the full permission: users.{module}
    const fullPermission = `users.${module}` as DjangoPermission;
    return user.permissions.includes(fullPermission);
  }

  /**
   * Get all modules the user can access (internal method)
   * @returns {ModulePermission[]} - Array of module permission codenames
   */
  private _getUserModules(): ModulePermission[] {
    const user = this._user();
    if (!user) return [];

    const modulePermissions: ModulePermission[] = [
      'view_orientador_module',
      'view_farmaceuta_module',
      'view_supervisor_module',
      'view_auditoria_module',
      'view_inteligencia_module',
    ];

    // Superusers can access all modules
    if (user.is_superuser) return modulePermissions;

    return modulePermissions.filter((module) => {
      const fullPermission = `users.${module}`;
      return user.permissions.includes(fullPermission);
    });
  }

  /**
   * Get user's display name
   * @returns {string} - User's full name or username
   */
  public getUserDisplayName(): string {
    const user = this._user();
    if (!user) return 'Usuario';

    // Prefer first_name + last_name
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    if (fullName) return fullName;

    // Fallback to username
    return user.username;
  }
}
